import { AbstractControl, FormGroup, FormControl, FormArray } from '@angular/forms';
import {
  FormCompilerStructure,
  FormStructure,
  FormStructureType,
  FormStructureValueType,
  FormControlStructure,
  FormGroupStructure,
  FormArrayStructure,
} from './form-structure';
import _ from 'lodash';
import { UniqueId } from '../uniqueId';
import { ConvertToDate } from '../dates';

interface GenerateOptions {
  /**
   * `data` is the complete record — an arrangement as just loaded from the
   * server — so a FormArray it has no entry for is emptied, the same way a
   * control it has no value for is reset to its default. A reload into an
   * existing form then yields the same form as a first load.
   *
   * Off by default: callers that regenerate a sub-form from `{}` or from a
   * `form.value` (which omits disabled controls) rely on arrays surviving.
   *
   * #271: without this, an arrangement reopened from the list kept a transfer
   * that had been added but never saved. The sparse-save snapshot then recorded
   * it as server state, its `_meta.id` was never sent, and the server wrote its
   * line item with no parent transfer — arrangement 4211 could not be opened.
   */
  replaceMissingArrays?: boolean;
}

class FormGenerator {
  constructor() {}

  static addToFormArray(formArray: FormArray, formGroup: FormGroup, data?: any): void {
    const uniqueId = new UniqueId();

    const id: string = uniqueId.getUniqueInArray(formArray.value, '_meta.id', 'id-');
    const index: number = formArray.value.length;

    // Only add `_meta` if the formGroup doesn't have it already
    if (formGroup.get('_meta') === null) {
      formGroup.addControl(
        '_meta',
        new FormGroup({
          id: new FormControl(),
          index: new FormControl(),
          deleted: new FormControl(),
        })
      );
    }

    if (data && data._meta !== undefined) {
      console.error(
        `You provided a _meta property when adding a new item to a FormArray. That isn't the correct procedure for using FormGenerator.addToFormArray(). The _meta data will be added automatically.`
      );
    }

    // onlySelf on the patches: the formGroup isn't attached to formArray yet,
    // so there is no ancestor chain to walk — the group's own value/status
    // cache is still recomputed by its own patchValue, and the push() below
    // refreshes formArray and its ancestors exactly as before.
    if (!data) {
      formGroup.patchValue({ _meta: { id, index } }, { onlySelf: true });

      formArray.push(formGroup);
    } else {
      formGroup.patchValue({ ...data, _meta: { id, index } }, { onlySelf: true });

      formArray.push(formGroup);
    }
  }

  static createFormControl(
    target: FormGroup,
    basePath: string,
    targetPath: string,
    name: string,
    control: FormControl | FormGroup | FormArray
  ): void {
    if (!target.get(targetPath)) {
      const baseFormGroup = (basePath ? target.get(basePath) : target) as FormGroup;
      if (baseFormGroup) {
        baseFormGroup.addControl(name, control);
      } else {
        console.error(
          `We don't have a targetPath of '${targetPath}'. When we tried to add the new control '${basePath}' we couldn't get it.`
        );
      }
    } else {
      // console.warn(`Control '${name}' for path '${targetPath}' already exists.`);
    }
  }

  generate(
    structure: FormStructure,
    data: { [key: string]: any },
    form?: FormGroup,
    options: GenerateOptions = {}
  ): FormGroup {
    if (!form) {
      form = new FormGroup({});
    }

    this.generateRecursive(structure, data, form, options);

    // PERF (2026-09: arrangement-open trace): the per-control patches inside
    // generateRecursive() run with { onlySelf: true }, so they no longer walk
    // updateValueAndValidity up the whole ancestor chain for EVERY leaf
    // (O(controls x depth) on the arrangement form). That leaves the .value /
    // .status caches of ancestor FormGroups/FormArrays stale for controls that
    // were PATCHED (controls that were ADDED refresh their parent via
    // addControl/push, as before). Recompute the caches once, children-first,
    // silently — Angular has no public deep updateValueAndValidity.
    FormGenerator.refreshValueAndValidityDeep(form);

    // The old per-leaf propagation also refreshed any ancestors ABOVE a
    // passed-in `form` (callers regenerate into subgroups of larger forms,
    // e.g. transfer-item's addressGroup). One silent non-onlySelf pass from
    // the root reproduces that: it recomputes `form` from its (now fresh)
    // children and propagates upward, without emitting any events the old
    // implementation didn't emit (all its patches were emitEvent: false).
    form.updateValueAndValidity({ emitEvent: false });

    return form;
  }

  private generateRecursive(
    structure: FormStructure,
    data: { [key: string]: any },
    form: FormGroup,
    options: GenerateOptions
  ): FormGroup {
    for (const key in structure) {
      if (structure.hasOwnProperty(key)) {
        const structureItem: FormControlStructure | FormGroupStructure | FormArrayStructure =
          structure[key];
        const path = key;

        if (structureItem.type === FormStructureType.FormControl) {
          let controlValue = null;

          if (_.has(data, path)) {
            controlValue = _.get(data, path);
          } else if (structureItem.value !== undefined) {
            controlValue = structureItem.value;
          }

          if (controlValue && structureItem.valueAs) {
            controlValue = this.parseFormStructureValueAs(controlValue, structureItem.valueAs);
          }

          // Check if form already has the control
          if (form.get(key)) {
            // If it does, patch the value.
            // onlySelf: the deep refresh at the end of generate() recomputes
            // ancestor caches once instead of on every leaf patch.
            form.get(key)?.patchValue(controlValue, { onlySelf: true, emitEvent: false });
          } else {
            // If it doesn't, add the control
            form.addControl(key, new FormControl(controlValue), { emitEvent: false });
          }

          // No `<key>__pathKey` marker control any more (#238): the save
          // payload resolves markers from the structure itself
          // (forms/path-key-markers.ts), so they cannot be lost to a reset().
        } else if (structureItem.type === FormStructureType.FormArray) {
          // TODO:
          // 1.a Check if we already have a form array
          // 1.b If we do, set it as the destination form array
          // 1.c If we don't, create a new form array

          // 2.a Start looping through the data
          // 2.b Check if we have a form group at the form array index
          // 2.c If we do, use it as the source for the generate function
          // 2.d If we don't, create a new form group and use it as the source for the generate function

          // 3.a If we created the form group in step 2.d, add it to the form array

          // 4.a If we created the form array in step 1.c, add it to the form

          // 1.a, 1.b, 1.c
          let mustAddFormArray = true;
          let destinationFormArray = new FormArray([]);

          if (form.get(key) instanceof FormArray) {
            destinationFormArray = form.get(key) as FormArray;

            mustAddFormArray = false;
          }

          // 2.a
          if (_.has(data, path)) {
            let dataArrayValue = _.get(data, path);

            // Convert the value to an array if it isn't already
            if (!_.isArray(dataArrayValue)) {
              dataArrayValue = [dataArrayValue];
            }

            // Ensure FormArray length matches data
            while (destinationFormArray.length > dataArrayValue.length) {
              destinationFormArray.removeAt(destinationFormArray.length - 1);
            }

            for (let i = 0; i < dataArrayValue.length; i++) {
              const dataValue = dataArrayValue[i];

              let mustAddFormGroup = true;

              let formGroup;

              // 2.b
              if (destinationFormArray.at(i)) {
                // 2.c
                formGroup = destinationFormArray.at(i) as FormGroup;

                mustAddFormGroup = false;
              } else {
                // 2.d
                formGroup = new FormGroup({});
              }

              this.generateRecursive(
                structureItem.value as FormStructure,
                dataValue,
                formGroup,
                options
              );

              if (mustAddFormGroup) {
                // 3.a
                destinationFormArray.push(formGroup);
              }
            }
          } else if (options.replaceMissingArrays) {
            // The record has no entries here, so neither does the form.
            while (destinationFormArray.length > 0) {
              destinationFormArray.removeAt(destinationFormArray.length - 1);
            }
          }

          // 4.a
          if (mustAddFormArray) {
            form.addControl(key, destinationFormArray);
          }
        } else if (structureItem.type === FormStructureType.FormGroup) {
          const formGroupData = _.has(data, path) ? data[path] : {};

          // I don't think this is needed anymore but lets keep it for now
          if (formGroupData && formGroupData.compiler) {
            structureItem.value = _.assign({}, structureItem.value, _.cloneDeep(FormCompilerStructure));
          }

          // Check if form already has the control
          if (form.get(key)) {
            const destinationFormGroup = form.get(key) as FormGroup;

            let keysA = Object.keys(destinationFormGroup.value);
            let keysB = new Set(Object.keys(formGroupData));

            if (keysB.size > 0) {
              // Find keys in 'a' that are not in 'b'
              let missingKeys = keysA.filter((k) => !keysB.has(k));

              for (const missingKey of missingKeys) {
                const v = destinationFormGroup.get(missingKey)?.value;

                let keys: string[] = [];
                let isArray: boolean = false;
                let isDate: boolean = false;

                if (v) {
                  keys = Object.keys(v);
                  isArray = Array.isArray(v);
                  isDate = v instanceof Date;
                }

                if (v && keys.length === 0 && isArray === false && isDate === false) {
                  if (typeof v !== 'object') {
                    console.error(
                      'We have a missing key and the value is empty. Should remove the control or set it to null?'
                    );
                    console.error(`missingKey: ${missingKey}`);
                    console.error(`value: ${JSON.stringify(v)}`);

                    // I think setting the value to null is better than removing the control
                    if (destinationFormGroup.get(missingKey)?.value) {
                      destinationFormGroup
                        .get(missingKey)
                        ?.patchValue(null, { onlySelf: true, emitEvent: false });
                    }

                    // destinationFormGroup.removeControl(missingKey);
                  }
                }
              }
            }

            this.generateRecursive(
              structureItem.value as FormStructure,
              formGroupData,
              destinationFormGroup,
              options
            );
          } else {
            const formGroup = this.generateRecursive(
              structureItem.value as FormStructure,
              formGroupData,
              new FormGroup({}),
              options
            );

            form.addControl(key, formGroup);
          }
        } else {
          console.error(
            `The supplied FormStructureType (${(structureItem as any).type}) isn't supported by generateForm()`
          );
        }
      }
    }

    return form;
  }

  /**
   * Recompute the .value/.status caches of every FormGroup/FormArray in the
   * tree, children-first, without emitting events and without propagating to
   * parents (each node is visited exactly once). Leaf FormControls are skipped:
   * their own caches are always fresh (setValue/patchValue and the FormControl
   * constructor run updateValueAndValidity on the control itself), and skipping
   * them avoids re-triggering their (possibly async) validators a second time.
   *
   * This is the "recompute once" half of the onlySelf patches in
   * generateRecursive() — see generate().
   */
  private static refreshValueAndValidityDeep(control: AbstractControl): void {
    if (control instanceof FormGroup) {
      const controls = control.controls;

      for (const key of Object.keys(controls)) {
        FormGenerator.refreshValueAndValidityDeep(controls[key]);
      }
    } else if (control instanceof FormArray) {
      for (const child of control.controls) {
        FormGenerator.refreshValueAndValidityDeep(child);
      }
    } else {
      // Leaf FormControl — nothing to do (see doc comment above)
      return;
    }

    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  private parseFormStructureValueAs(value: any, type: FormStructureValueType): any {
    if (type === FormStructureValueType.Date) {
      if (!value) {
        // console.warn('Value should have been a date, but the value wasn\'t set.', value);

        return value;
      }

      const date = ConvertToDate(value);

      return date;
    } else {
      console.error(
        `The supplied FormStructureValueType (${type}) isn't supported by parseFormStructureValueAs()`
      );
    }
  }
}

export { FormGenerator, GenerateOptions };
